import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Ban, MailX } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  CLOSE_REASON_MIN,
  JOB_CLOSE_OUTCOMES,
  JOB_CLOSE_OUTCOME_LABELS,
  JOB_STATUS_LABELS,
  OFFER_STATUS_LABELS,
  type JobCloseOutcome,
  type ClosureView,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Checkbox } from '../../../components/Checkbox';
import { Chip } from '../../../components/Chip';
import { Field } from '../../../components/Field';
import { Textarea } from '../../../components/Input';
import { Popover, usePopover } from '../../../components/Popover';
import { Segmented } from '../../../components/Segmented';
import { Skeleton, SkeletonLines } from '../../../components/Skeleton';
import { JOB_STATUS_TONES, STAGE_TONES } from '../../../components/status-tones';
import { TagInput } from '../../../components/TagInput';
import { ToneChip } from '../../../components/Tone';
import { ForbiddenState, NotFoundState, ServerErrorState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { formatIsoDate } from '../../../lib/format';
import { useToasts } from '../../../stores/toasts';
import { CLOSURE_KEY, HIRES_KEY, hiresApi, useClosure } from '../api/hires-api';
import styles from '../components/Hires.module.css';
import { useModule, useStageLabels } from '../../../lib/modules';
import { cancelQuestion, outcomeText, type ClosureModules } from '../closure-text';

/**
 * OFR-05 — F13.15 and F13.16 (§L13, S24 Q5). An open job closes partly filled or cancelled:
 * everyone still running becomes "Position no longer available", never "Not selected", booked
 * interviews are cancelled with an apology, and the unfilled seats go back to the position
 * ledger. A job the last joining filled closes out its remaining candidates as "position
 * filled by another candidate". Either way, one click sends the letters and anyone who reached
 * an interview is suggested for a pool. Offers out with a candidate block it until settled.
 * Hires, pools, the position ledger and the hiring request show only while their modules are on.
 */
export function ClosurePage({ jobId }: { readonly jobId: string }): ReactNode {
  const query = useClosure(jobId);
  if (query.isPending) return <ClosureSkeleton />;
  if (query.error) {
    if (query.error.status === 403) return <ForbiddenState resource="this job" contact="Your admin" />;
    if (query.error.status === 404) return <NotFoundState />;
    return <ServerErrorState onRetry={() => void query.refetch()} />;
  }
  return <ClosureScreen view={query.data} />;
}

function ClosureScreen({ view }: { readonly view: ClosureView }): ReactNode {
  const stageLabels = useStageLabels();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toasts = useToasts();
  const modules: ClosureModules = {
    isOffersOn: useModule('offers'),
    isPoolsOn: useModule('pools'),
    isOrganisationOn: useModule('organisation'),
    isRequisitionsOn: useModule('requisitions'),
  };
  const { triggerRef: cancelRef, anchor: cancelAnchor, toggle: toggleCancel, close: closeCancel } = usePopover();
  const isFilledJob = view.status === 'filled';
  const [outcome, setOutcome] = useState<JobCloseOutcome>(view.outcomes[0] ?? 'closed_partial');
  const [reason, setReason] = useState('');
  const [isLetterSent, setIsLetterSent] = useState(view.isLetterSentByDefault);
  const [pooled, setPooled] = useState<ReadonlySet<string>>(() => new Set(view.remaining.filter((row) => row.isPoolSuggested).map((row) => row.applicationId)));
  const [poolNames, setPoolNames] = useState<readonly string[]>([]);
  const [isPending, setIsPending] = useState(false);

  const canAct = isFilledJob ? view.can.regret : view.can.close;
  const isCancel = !isFilledJob && outcome === 'cancelled';
  // §L13: a cancel always needs a reason; a partial close too while anyone is past screening.
  const isReasonRequired = isCancel || (!isFilledJob && view.hasPastScreening);
  const needsReason = isReasonRequired && reason.trim().length < CLOSE_REASON_MIN;
  const needsPool = modules.isPoolsOn && pooled.size > 0 && poolNames.length === 0;
  const withEmail = view.remaining.filter((row) => row.hasEmail).length;
  const emailed = isLetterSent ? withEmail : 0;

  async function submit(): Promise<void> {
    closeCancel();
    const byName = new Map(view.pools.map((pool) => [pool.name.toLowerCase(), pool.id]));
    const poolIds = poolNames.flatMap((name) => byName.get(name.toLowerCase()) ?? []);
    const newPoolNames = poolNames.filter((name) => !byName.has(name.toLowerCase()));
    // Sent as changes to the suggestion, so a job with hundreds still running stays a small request.
    const exceptIds = view.remaining.filter((row) => row.isPoolSuggested && !pooled.has(row.applicationId)).map((row) => row.applicationId);
    const alsoIds = view.remaining.filter((row) => !row.isPoolSuggested && pooled.has(row.applicationId)).map((row) => row.applicationId);
    setIsPending(true);
    try {
      const result = await hiresApi.closeOut(view.jobId, {
        version: view.version,
        outcome: isFilledJob ? null : outcome,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        isLetterSent,
        ...(modules.isPoolsOn ? { pool: { exceptIds, alsoIds, poolIds, newPoolNames } } : {}),
      });
      toasts.show({
        tone: 'success',
        message: isFilledJob ? 'Job closed out' : isCancel ? 'Job cancelled' : 'Job closed',
        detail: `${result.closed} ${result.closed === 1 ? 'candidate' : 'candidates'} closed, ${result.lettersQueued} ${result.lettersQueued === 1 ? 'letter' : 'letters'} sent${modules.isPoolsOn ? `, ${result.pooled} added to a pool` : ''}${result.interviewsCancelled > 0 ? `, ${result.interviewsCancelled} interviews cancelled` : ''}.`,
      });
      await Promise.all([queryClient.invalidateQueries({ queryKey: CLOSURE_KEY }), queryClient.invalidateQueries({ queryKey: HIRES_KEY }), queryClient.invalidateQueries({ queryKey: ['jobs'] })]);
      await navigate({ to: '/jobs/$jobId', params: { jobId: view.jobId } });
    } catch (error) {
      toasts.show({ tone: 'danger', message: 'The job was not closed', detail: errorMessage(error) });
    } finally {
      setIsPending(false);
    }
  }

  function togglePool(applicationId: string, isOn: boolean): void {
    setPooled((current) => {
      const next = new Set(current);
      if (isOn) next.add(applicationId);
      else next.delete(applicationId);
      return next;
    });
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/jobs/$jobId" params={{ jobId: view.jobId }} className={styles.back}>
          <ArrowLeft size={14} aria-hidden />
          {view.jobTitle}
        </Link>
        <div className={styles.titleLine}>
          <h1 className={styles.title}>{isFilledJob ? `Close out ${view.jobTitle}` : `Close ${view.jobTitle}`}</h1>
          <ToneChip tone={JOB_STATUS_TONES[view.status]}>{JOB_STATUS_LABELS[view.status]}</ToneChip>
        </div>
        <p className={styles.meta}>
          {view.reference} · {view.places.filled} of {view.places.total} {view.places.total === 1 ? 'place' : 'places'} filled
          {view.places.waitingToJoin > 0 ? ` · ${view.places.waitingToJoin} waiting to join` : ''}
        </p>
      </header>

      {view.liveOffers.length > 0 && (
        <div className={styles.warning}>
          <p className={styles.subhead}>Settle these offers first — withdraw them, or wait for the joining:</p>
          <ul className={styles.missing}>
            {view.liveOffers.map((offer) => (
              <li key={offer.offerId}>
                <Link to="/offers/$offerId" params={{ offerId: offer.offerId }} className={styles.fileLink}>
                  {offer.candidateName}
                </Link>{' '}
                — {OFFER_STATUS_LABELS[offer.status].toLowerCase()}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ADR 0083: with Offers off, the selected are the hires, and closing as partly filled waits for their plans. */}
      {!isFilledJob && outcome === 'closed_partial' && view.plansNeeded.length > 0 && (
        <div className={styles.warning}>
          <p className={styles.subhead}>Before the job closes, write the 30 and 90-day plans for:</p>
          <ul className={styles.missing}>
            {view.plansNeeded.map((hire) => (
              <li key={hire.applicationId}>
                <Link to="/hires/$applicationId/plans" params={{ applicationId: hire.applicationId }} className={styles.fileLink}>
                  {hire.candidateName}
                </Link>
              </li>
            ))}
          </ul>
          <p className={styles.quiet}>The job’s line manager writes them on each person’s plans page. HR can too.</p>
        </div>
      )}

      <div className={modules.isOffersOn ? styles.columns : `${styles.columns} ${styles.single}`}>
        <div className={styles.main}>
          {!canAct && view.liveOffers.length === 0 && <p className={styles.statusLine}>{doneText(view)}</p>}
          {canAct && (
            <Card title={isFilledJob ? 'Everyone still in the running' : 'How it closes'}>
              <div className={styles.form}>
                {!isFilledJob && (
                  <>
                    <Segmented label="How it closes" value={outcome} options={view.outcomes.map((value) => ({ value, label: JOB_CLOSE_OUTCOME_LABELS[value] }))} onChange={setOutcome} />
                    <p className={styles.quiet}>{outcomeText(outcome, view, modules)}</p>
                    <Field
                      label={isCancel ? 'Why is it cancelled?' : 'Why close it now?'}
                      isRequired={isReasonRequired}
                      isOptional={!isReasonRequired}
                      {...(!isCancel && isReasonRequired ? { hint: 'Someone here is past screening, so a written reason is needed.' } : {})}
                    >
                      {({ inputId, describedBy }) => (
                        <Textarea
                          id={inputId}
                          aria-describedby={describedBy}
                          rows={2}
                          maxLength={500}
                          value={reason}
                          placeholder={isCancel ? 'The budget for this role was withdrawn.' : 'Two of three places filled; the third is not needed this year.'}
                          onChange={(event) => setReason(event.target.value)}
                        />
                      )}
                    </Field>
                  </>
                )}
                {isFilledJob && <p className={styles.quiet}>Every place is filled. They become "Not selected — position filled by another candidate", with a warm letter.</p>}
                <Checkbox checked={isLetterSent} onChange={setIsLetterSent} label={`Email everyone still in the running (${withEmail} with an email address)`} />
                {modules.isPoolsOn && view.remaining.length > 0 && (
                  <Field label="Add to a talent pool" isOptional={pooled.size === 0} isRequired={pooled.size > 0} hint="Anyone who reached an interview is ticked below — they cost the most to find.">
                    {({ inputId, describedBy }) => (
                      <TagInput id={inputId} describedBy={describedBy} value={poolNames} onChange={setPoolNames} placeholder="Choose a pool, or type a new name" suggestions={view.pools.map((pool) => pool.name)} maxItems={5} maxLength={80} isInvalid={needsPool} />
                    )}
                  </Field>
                )}
                <div className={styles.actions}>
                  {isCancel ? (
                    // Cancelling can't be undone and emails everyone, so it asks once, beside the button (CLAUDE.md §5).
                    <>
                      <Button ref={cancelRef} variant="dangerSolid" icon={Ban} disabled={needsReason || needsPool} isPending={isPending} aria-haspopup="dialog" onClick={toggleCancel}>
                        {buttonText(isFilledJob, outcome, view.remaining.length)}
                      </Button>
                      <Popover anchor={cancelAnchor} onClose={closeCancel} align="left" label="Cancel the job">
                        <div className={styles.popoverBody}>
                          <p className={styles.confirmTitle}>{cancelQuestion(emailed)}</p>
                          <p className={styles.quiet}>This can't be undone.</p>
                          <div className={styles.confirmActions}>
                            <Button variant="ghost" size="sm" onClick={closeCancel}>
                              Keep it open
                            </Button>
                            <Button variant="dangerSolid" size="sm" icon={Ban} onClick={() => void submit()}>
                              Cancel the job
                            </Button>
                          </div>
                        </div>
                      </Popover>
                    </>
                  ) : (
                    <Button variant="solid" icon={MailX} disabled={needsReason || needsPool} isPending={isPending} onClick={() => void submit()}>
                      {buttonText(isFilledJob, outcome, view.remaining.length)}
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          )}
          {view.remaining.length > 0 && (
            <Card title="Still in the running" meta={`${view.remaining.length} ${view.remaining.length === 1 ? 'person' : 'people'}`}>
              <ul className={`${styles.people} ${styles.runningList}`}>
                {view.remaining.map((row) => (
                  <li key={row.applicationId} className={styles.person}>
                    <span className={styles.personName}>
                      {row.candidateName}
                      <Chip tone={STAGE_TONES[row.stage]} plain>
                        {stageLabels[row.stage]}
                      </Chip>
                      {!row.hasEmail && (
                        <Chip tone="warning" plain>
                          No email
                        </Chip>
                      )}
                    </span>
                    {canAct && modules.isPoolsOn && <Checkbox checked={pooled.has(row.applicationId)} onChange={(isOn) => togglePool(row.applicationId, isOn)} label="Pool" />}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
        {modules.isOffersOn && (
          <div className={styles.side}>
            <Card title="Hires">
              {view.hires.length === 0 ? (
                <p className={styles.quiet}>Nobody has accepted an offer on this job.</p>
              ) : (
                <ul className={styles.people}>
                  {view.hires.map((hire) => (
                    <li key={hire.offerId} className={styles.person}>
                      <Link to="/offers/$offerId/joining" params={{ offerId: hire.offerId }} className={styles.fileLink}>
                        {hire.name}
                      </Link>
                      <span className={styles.meta}>{hire.joinedOn ? `Joined ${formatIsoDate(hire.joinedOn)}` : 'Waiting to join'}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            {view.unsentOfferCount > 0 && !isFilledJob && <p className={styles.quiet}>{view.unsentOfferCount} unsent {view.unsentOfferCount === 1 ? 'offer is' : 'offers are'} withdrawn with the close.</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function buttonText(isFilledJob: boolean, outcome: JobCloseOutcome, count: number): string {
  const people = `${count} ${count === 1 ? 'person' : 'people'}`;
  if (isFilledJob) return `Close out ${people}`;
  return outcome === 'cancelled' ? 'Cancel the job' : `Close the job${count > 0 ? ` and ${people}` : ''}`;
}

function doneText(view: ClosureView): string {
  if (view.status === 'filled') return 'Every place is filled and nobody is left in the running.';
  if (JOB_CLOSE_OUTCOMES.some((outcome) => outcome === view.status)) return `This job is ${JOB_STATUS_LABELS[view.status].toLowerCase()}.`;
  return 'Only HR closes an open job. A job fills by itself when its last hire joins.';
}

function ClosureSkeleton(): ReactNode {
  return (
    <div className={styles.page}>
      <Skeleton shape="title" width="40%" />
      <SkeletonLines count={2} />
      <SkeletonLines count={8} />
    </div>
  );
}
